import {memo, useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import useRequireAuth from "@/hooks/useRequireAuth";
import {avatarImages} from "@/util/avatarUtils";
import styles from "./LiveBetFeed.module.css";

const MOCK_BETS = [
  {user: "rafael", alias: "rafael", game: "Crash", bet: 20, result: "loss", avatar: avatarImages[0]},
  {user: "maria", alias: "maria", game: "Sweet Bonanza", bet: 120, result: "win", avatar: avatarImages[1]},
  {user: "carlos", alias: "carlos", game: "Roulette", bet: 10, result: "loss", avatar: avatarImages[2]},
  {user: "lucas", alias: "lucas", game: "Blackjack", bet: 250, result: "win", avatar: avatarImages[3]},
  {user: "sofia", alias: "sofia", game: "Wolf Gold", bet: 35, result: "win", avatar: avatarImages[4]},
  {user: "diego", alias: "diego", game: "Big Bass", bet: 15, result: "loss", avatar: avatarImages[0]},
  {user: "valentina", alias: "valentina", game: "Olympus", bet: 180, result: "win", avatar: avatarImages[1]},
  {user: "andres", alias: "andres", game: "Sugar Rush", bet: 45, result: "loss", avatar: avatarImages[2]},
  {user: "daniela", alias: "daniela", game: "Fire Joker", bet: 300, result: "win", avatar: avatarImages[3]},
  {user: "miguel", alias: "miguel", game: "Razor Shark", bet: 25, result: "loss", avatar: avatarImages[4]},
  {user: "isabella", alias: "isabella", game: "Dog House", bet: 60, result: "win", avatar: avatarImages[0]},
  {user: "jorge", alias: "jorge", game: "Book of Dead", bet: 90, result: "loss", avatar: avatarImages[1]},
];

const VISIBLE_COUNT = 8;
const ROTATE_INTERVAL = 3000;
const BIG_WIN_THRESHOLD = 200;

const BetRow = memo(({user, alias, game, bet, result, avatar, isSelected, onSelect}) => {
  const isWin = result === "win";
  const isBigWin = isWin && bet >= BIG_WIN_THRESHOLD;

  return (
    <div
      className={`${styles.row} ${isBigWin ? styles.bigWin : ""} ${isSelected ? styles.rowSelected : ""}`}
      onClick={() => onSelect(alias)}
      onKeyDown={e => e.key === "Enter" && onSelect(alias)}
      role="button"
      tabIndex={0}
    >
      <div className={styles.avatar}>
        <img
          src={avatar}
          alt={user}
          className={styles.avatarImg}
        />
      </div>
      <div className={styles.betInfo}>
        <span className={styles.betText}>
          <span className={styles.betUser}>{user}</span>
          {isWin ? " ganó " : " apostó "}
          <span className={styles.betGame}>{game}</span>
        </span>
      </div>
      <span className={`${styles.betAmount} ${isWin ? styles.betWin : styles.betLoss}`}>
        {isWin ? "+" : ""}
        {bet} Bs
      </span>
    </div>
  );
});

BetRow.displayName = "BetRow";

const LiveBetFeed = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const [offset, setOffset] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const intervalRef = useRef(null);
  const actionBarRef = useRef(null);

  const rotate = useCallback(() => {
    setOffset(prev => (prev + 1) % MOCK_BETS.length);
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(rotate, ROTATE_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [rotate]);

  // Pause rotation when a user is selected
  useEffect(() => {
    if (selectedUser) {
      clearInterval(intervalRef.current);
    } else {
      intervalRef.current = setInterval(rotate, ROTATE_INTERVAL);
    }
    return () => clearInterval(intervalRef.current);
  }, [selectedUser, rotate]);

  // Close action bar on outside click
  useEffect(() => {
    if (!selectedUser) return;
    const handleClick = e => {
      if (actionBarRef.current && !actionBarRef.current.contains(e.target)) {
        setSelectedUser(null);
      }
    };
    document.addEventListener("pointerdown", handleClick);
    return () => document.removeEventListener("pointerdown", handleClick);
  }, [selectedUser]);

  const handleSelect = useCallback(alias => {
    setSelectedUser(prev => (prev === alias ? null : alias));
  }, []);

  const selectedBet = selectedUser ? MOCK_BETS.find(b => b.alias === selectedUser) : null;

  const visibleBets = [];
  for (let i = 0; i < VISIBLE_COUNT; i++) {
    visibleBets.push(MOCK_BETS[(offset + i) % MOCK_BETS.length]);
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span
          className={styles.headerIcon}
          role="img"
          aria-label="lightning"
        >
          ⚡
        </span>
        <h3 className={styles.headerTitle}>Actividad en Vivo</h3>
        <span className={styles.liveBadge}>Live</span>
      </div>

      {selectedBet && (
        <div
          className={styles.actionBar}
          ref={actionBarRef}
        >
          <div className={styles.actionBarPlayer}>
            <img
              src={selectedBet.avatar}
              alt={selectedBet.user}
              className={styles.actionBarAvatar}
            />
            <span className={styles.actionBarName}>{selectedBet.user}</span>
          </div>
          <div className={styles.actionBarBtns}>
            <button
              type="button"
              className={styles.actionBarBtn}
              onClick={() => {
                setSelectedUser(null);
                requireAuth(() => navigate(`/u/${selectedBet.alias}`));
              }}
            >
              👤 {t("promoViewProfile", {defaultValue: "Perfil"})}
            </button>
            <button
              type="button"
              className={styles.actionBarBtn}
              onClick={() => {
                setSelectedUser(null);
                requireAuth(() => navigate(`/u/${selectedBet.alias}`));
              }}
            >
              <span className={styles.actionBarStoryDot} />
              {t("promoViewStories", {defaultValue: "Historias"})}
            </button>
            <button
              type="button"
              className={styles.actionBarBtn}
              onClick={() => {
                setSelectedUser(null);
                requireAuth(() => navigate("/user/contacts"));
              }}
            >
              ➕ {t("promoAddContact", {defaultValue: "Añadir"})}
            </button>
          </div>
        </div>
      )}

      <div className={styles.feedList}>
        {visibleBets.map((bet, i) => (
          <BetRow
            key={`${bet.user}-${offset + i}`}
            {...bet}
            isSelected={selectedUser === bet.alias}
            onSelect={handleSelect}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(LiveBetFeed);
